/**
 * LayoutBootstrap
 *
 * Bootstrap component for authenticated layout routes (dashboard, workbooks, passages, etc.).
 * Calls session-init once to seed:
 *   - AuthContext credits (via updateCredits)
 *   - NotificationContext (initial unread count + admin badge counts)
 *   - usePassageLimitsStore (passage word limits + one-click menu switch)
 *
 * Wraps children with NotificationProvider that receives the seeded initial data,
 * eliminating redundant initial fetch calls that previously caused 8-10 simultaneous
 * API requests on page load.
 *
 * Phase 3 implementation: session-init integration.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { fetchSessionInit } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { usePassageLimitsStore } from '@/stores/usePassageLimitsStore';
import type { AdminBadgeCounts } from '@/services/adminPageService';
import { DEFAULT_PASSAGE_MIN_WORDS, DEFAULT_PASSAGE_MAX_WORDS } from '@/constants/passage';
import { prefetchEditorInit } from './editorSeedCache';

interface LayoutBootstrapProps {
  children: ReactNode;
  prefetchEditor?: boolean;
}

interface SeedState {
  unreadCount: number | undefined;
  adminBadgeCounts: AdminBadgeCounts | null | undefined;
  ready: boolean;
  ownerUserId: number | undefined;
  creditsToSeed: number | undefined;
  passageLimitsSeed: { minWords: number; maxWords: number; oneClickMenuPublic: boolean } | undefined;
}

// Module-level cache so session-init is fetched only once per SPA session,
// even though LayoutBootstrap remounts on every route navigation.
let sessionInitPromise: Promise<SeedState> | null = null;
let cachedSeed: SeedState | null = null;
let sessionInitPromiseUserId: number | undefined;

function getCachedLayoutSeed(userId: number | undefined): SeedState | undefined {
  if (cachedSeed && cachedSeed.ownerUserId === userId) {
    return cachedSeed;
  }
  return undefined;
}

function getSessionInitPromise(
  userId: number | undefined,
): Promise<SeedState> {
  const currentCachedSeed = getCachedLayoutSeed(userId);
  if (currentCachedSeed) return Promise.resolve(currentCachedSeed);
  if (sessionInitPromise && sessionInitPromiseUserId === userId) return sessionInitPromise;

  if (!userId) {
    const seed: SeedState = {
      unreadCount: undefined,
      adminBadgeCounts: undefined,
      ready: true,
      ownerUserId: userId,
      creditsToSeed: undefined,
      passageLimitsSeed: undefined,
    };
    cachedSeed = seed;
    return Promise.resolve(seed);
  }

  sessionInitPromiseUserId = userId;
  sessionInitPromise = fetchSessionInit()
    .then((data) => {
      const seed: SeedState = {
        unreadCount: data.notification_counts.unread_count,
        adminBadgeCounts: data.notification_counts.admin_badge_counts,
        ready: true,
        ownerUserId: userId,
        creditsToSeed: data.credits?.credits,
        passageLimitsSeed: {
          minWords: data.public_settings.passage_min_words ?? DEFAULT_PASSAGE_MIN_WORDS,
          maxWords: data.public_settings.passage_max_words ?? DEFAULT_PASSAGE_MAX_WORDS,
          oneClickMenuPublic: data.public_settings.oneclick_mocktest_menu_public === true,
        },
      };
      cachedSeed = seed;
      sessionInitPromise = null;
      sessionInitPromiseUserId = undefined;
      return seed;
    })
    .catch(() => {
      const seed: SeedState = {
        unreadCount: undefined,
        adminBadgeCounts: undefined,
        ready: true,
        ownerUserId: userId,
        creditsToSeed: undefined,
        passageLimitsSeed: undefined,
      };
      cachedSeed = seed;
      sessionInitPromise = null;
      sessionInitPromiseUserId = undefined;
      return seed;
    });

  return sessionInitPromise;
}

// eslint-disable-next-line react-refresh/only-export-components
export function __resetLayoutBootstrapCacheForTests() {
  sessionInitPromise = null;
  cachedSeed = null;
  sessionInitPromiseUserId = undefined;
}

// @MX:ANCHOR: LayoutBootstrap - session-init seed entry point for authenticated routes
// @MX:REASON: Public API boundary; orchestrates session-init and distributes data to multiple contexts
export function LayoutBootstrap({ children, prefetchEditor = false }: LayoutBootstrapProps) {
  const { user, credits, updateCredits } = useAuth();

  const [seed, setSeed] = useState<SeedState>(() => {
    // Use cached seed immediately if available (avoids loading flash on navigation)
    const currentCachedSeed = getCachedLayoutSeed(user?.id);
    if (currentCachedSeed) return currentCachedSeed;
    return {
      unreadCount: undefined,
      adminBadgeCounts: undefined,
      ready: false,
      ownerUserId: user?.id,
      creditsToSeed: undefined,
      passageLimitsSeed: undefined,
    };
  });

  useEffect(() => {
    if (!seed.ready || seed.ownerUserId !== user?.id) {
      return;
    }

    if (seed.creditsToSeed !== undefined && credits === null) {
      updateCredits(seed.creditsToSeed);
    }

    if (seed.passageLimitsSeed && !usePassageLimitsStore.getState().loaded) {
      usePassageLimitsStore.setState({
        minWords: seed.passageLimitsSeed.minWords,
        maxWords: seed.passageLimitsSeed.maxWords,
        oneClickMenuPublic: seed.passageLimitsSeed.oneClickMenuPublic,
        loaded: true,
      });
    }
  }, [seed.ready, seed.ownerUserId, seed.creditsToSeed, seed.passageLimitsSeed, user?.id, credits, updateCredits]);

  useEffect(() => {
    if (seed.ownerUserId !== user?.id) {
      const currentCachedSeed = getCachedLayoutSeed(user?.id);
      if (currentCachedSeed) {
        setSeed(currentCachedSeed);
      } else {
        setSeed({
          unreadCount: undefined,
          adminBadgeCounts: undefined,
          ready: false,
          ownerUserId: user?.id,
          creditsToSeed: undefined,
          passageLimitsSeed: undefined,
        });
      }
      return;
    }

    if (seed.ready) return;

    let cancelled = false;

    // Editor routes can overlap their independent bootstrap request with
    // session-init instead of waiting for this layout gate to render children.
    if (prefetchEditor && user?.id) {
      void prefetchEditorInit(user.id);
    }

    getSessionInitPromise(user?.id).then((result) => {
      if (!cancelled) setSeed(result);
    });

    return () => { cancelled = true; };
  }, [user?.id, updateCredits, seed.ready, seed.ownerUserId, prefetchEditor]);

  if (!seed.ready) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <NotificationProvider
      initialUnreadCount={seed.unreadCount}
      initialAdminBadgeCounts={seed.adminBadgeCounts}
    >
      {children}
    </NotificationProvider>
  );
}
